
export const resizeAndCompressImage = (file: File, targetSize: number = 800, maintainAspect: boolean = true): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    // Enhanced error handling for HEIC files
    img.onerror = (error) => {
      console.error('Image loading failed:', error);
      if (isHEICFile(file)) {
        reject(new Error('HEIC/HEIF files may not be supported in this browser. Please try converting to JPEG first, or use a different browser.'));
      } else {
        reject(new Error('Failed to load image. Please try a different image file.'));
      }
    };
    
    img.onload = () => {
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      let { width, height } = img;
      
      if (maintainAspect) {
        // Maintain aspect ratio, resize to fit within target size
        const scale = Math.min(targetSize / width, targetSize / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      } else {
        // Force square format
        const size = Math.min(width, height);
        const scale = targetSize / size;
        width = height = targetSize;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw the scaled image without background fill
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to blob with compression
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        
        // Check if compressed size is under 1MB
        if (blob.size <= 1024 * 1024) {
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        } else {
          // If still too large, try with lower quality
          canvas.toBlob((smallerBlob) => {
            if (!smallerBlob) {
              reject(new Error('Failed to create compressed blob'));
              return;
            }
            const compressedFile = new File([smallerBlob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          }, 'image/jpeg', 0.7);
        }
      }, 'image/jpeg', 0.85);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Square resize function that actually creates a square crop
export const resizeToSquare = (file: File, size: number = 512): Promise<File> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    // Enhanced error handling for HEIC files
    img.onerror = (error) => {
      console.error('Image loading failed:', error);
      if (isHEICFile(file)) {
        reject(new Error('HEIC/HEIF files may not be supported in this browser. Please try converting to JPEG first, or use a different browser.'));
      } else {
        reject(new Error('Failed to load image. Please try a different image file.'));
      }
    };
    
    img.onload = () => {
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      
      const { width, height } = img;
      
      canvas.width = size;
      canvas.height = size;
      
      // Calculate the square crop from the center
      const cropSize = Math.min(width, height);
      const cropX = (width - cropSize) / 2;
      const cropY = (height - cropSize) / 2;
      
      // Draw the cropped square image
      ctx.drawImage(
        img,
        cropX, cropY, cropSize, cropSize, // Source crop
        0, 0, size, size // Destination
      );
      
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create blob'));
          return;
        }
        
        const compressedFile = new File([blob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        resolve(compressedFile);
      }, 'image/jpeg', 0.9);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Simple resize that maintains aspect ratio - no forced square cropping
export const resizeToSquareWithPadding = (file: File, size: number = 800): Promise<File> => {
  return resizeAndCompressImage(file, size, true);
};

// Enhanced HEIC detection function
export const isHEICFile = (file: File): boolean => {
  const heicMimeTypes = [
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence'
  ];
  
  const heicExtensions = ['.heic', '.heif', '.HEIC', '.HEIF'];
  
  // Check MIME type first
  if (heicMimeTypes.includes(file.type)) {
    return true;
  }
  
  // Fallback to file extension if MIME type is not detected properly
  const fileName = file.name.toLowerCase();
  return heicExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
};

export const validateImageFile = (file: File): string | null => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
  
  // Check MIME type first
  if (!validTypes.includes(file.type)) {
    // Fallback to extension check for HEIC files (some browsers don't detect MIME type properly)
    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      return 'Please upload a JPEG, PNG, WebP, or HEIC image file.';
    }
  }
  
  if (file.size > 10 * 1024 * 1024) { // 10MB limit before processing
    return 'Image file is too large. Please choose a smaller image.';
  }
  
  return null;
};
